import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLang } from "@/contexts/AppContexts";
import { Search } from "lucide-react";

const severityClass = {
  low: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-200 dark:bg-amber-950/40 dark:border-amber-900",
  medium:
    "text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-200 dark:bg-orange-950/40 dark:border-orange-900",
  high: "text-red-700 bg-red-50 border-red-200 dark:text-red-200 dark:bg-red-950/40 dark:border-red-900",
};

const RegionsList = ({ regions = [], selectedId, onSelect }) => {
  const { t } = useLang();

  return (
    <Card className="overflow-hidden" data-testid="regions-panel">
      <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-display text-sm font-semibold">
            {t("regions.title")}
          </h3>
        </div>
        <span className="text-xs text-muted-foreground font-mono-plex tabular-nums">
          {regions.length}
        </span>
      </div>
      <ScrollArea className="max-h-[420px]">
        {regions.length === 0 ? (
          <p
            className="px-4 py-6 text-center text-sm text-muted-foreground"
            data-testid="regions-empty"
          >
            {t("regions.empty")}
          </p>
        ) : (
          <div className="divide-y">
            {regions.map((r, i) => {
              const isActive = selectedId === i;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onSelect && onSelect(i)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                    isActive
                      ? "bg-primary/5 border-l-2 border-primary"
                      : "hover:bg-muted/40"
                  }`}
                  data-testid="region-item"
                >
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-[10px] font-bold ${
                      severityClass[r.severity] || severityClass.medium
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">
                        {r.label || "Suspicious area"}
                      </p>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${
                          severityClass[r.severity] || severityClass.medium
                        }`}
                      >
                        {t(`viewer.severity_${r.severity || "medium"}`)}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {r.reason}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </Card>
  );
};

export { RegionsList };
export default RegionsList;
